import {
  availableAuditEntriesAtom,
  currentAuditEntryAtom,
  demoModeAtom,
  fullCdnPathAtom,
  fullUsersCdnPathAtom,
  jiraConfigAtom,
  reportDemoModeAtom,
  selectedDomainAtom,
  todosDataAtom,
  tokenExpiresAtom,
  userAtom,
} from '@/global-store';
import calculateAccessibilityData from '@/helpers/calculateAccessibilityData';
import formatDateToLocale from '@/helpers/formatDateToLocale';
import useDebounce from '@/hooks/useDebounce';
import { useAuditEntriesWithFallback } from '@/hooks/useAuditEntriesWithFallback';
import { defaultReportSettings } from '@helpers/constants';
import { useNotification } from '@eye-able/ui';
import {
  accessStorageUrl,
  api,
  demoUser,
  queryKeys,
  reportStorageUrl,
  storageUrl,
} from '@http/constants';
import fetchWrapper from '@http/fetchWrapper';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAtom } from 'jotai';
import clone from 'nanoclone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import packageJson from '../../package.json';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { useFetchRules } from '@/http/queries/useFetchRules';

const enabledAdmin = (user = {}) => !!(user.isAdmin || user.isSalesAdmin);
const enabledAdminPartner = (user = {}) =>
  enabledAdmin(user) || !!user.isPartner;
const enabledTeamAdmin = (user = {}) => {
  return enabledAdmin(user) || !!(user.isOrgAdmin || user.orgAdmin);
};

const version = packageJson.version;

/*
inspired by https://www.bigbinary.com/blog/react-query
*/

const offlineMode = import.meta.env.VITE_OFFLINE_MODE === 'true';
if (offlineMode) console.info('running in offline mode');

// --------------------------------------- Admin Queries ---------------------------------------
export function useFetchUsersAdmin(id = 'all') {
  return useQuery({
    queryKey: [api.users, id],
    queryFn: async () => {
      return id === 'all'
        ? fetchWrapper.get(api.users)
        : fetchWrapper.get(api.users, { id });
    },
  });
}

export function useFetchSecurityLogs(types) {
  const [user] = useAtom(userAtom);

  return useQuery({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin || user?.isPartner),
    queryKey: [api.securityLogs, types],
    queryFn: () => fetchWrapper.get(api.securityLogs, { types }),
  });
}

export function useFetchAccessLicensesAdmin() {
  const accessLicensesQuery = useQuery({
    queryKey: [api.accessLicenses],
    queryFn: () => fetchWrapper.get(api.accessLicenses),
  });

  const { auditEntries: availableAuditEntries } = useAuditEntriesWithFallback(
    (accessLicensesQuery.data ?? []).map((item) => item.reportId),
  );

  const data = useMemo(() => {
    if (!accessLicensesQuery.data) return accessLicensesQuery.data;

    const auditMap = new Map(
      availableAuditEntries.map((entry) => [entry.id, entry]),
    );

    return accessLicensesQuery.data
      .filter((item) => auditMap.has(item.reportId))
      .map((item) => ({
        ...item,
        domain: auditMap.get(item.reportId)?.domain,
      }));
  }, [accessLicensesQuery.data, availableAuditEntries]);

  return {
    data,
    isLoading: accessLicensesQuery.isLoading,
    isError: accessLicensesQuery.isError,
    error: accessLicensesQuery.error,
  };
}

export function useUpdateAccessLicensesAdmin() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => {
      return fetchWrapper.put(api.accessLicenses, body);
    },
    onSuccess: (data) => {
      notification.success('accessLicenses updated!');

      queryClient.setQueryData([api.accessLicenses], (oldData) => {
        if (!oldData) return [data];

        const newData = [...oldData];
        const index = newData.findIndex((entry) => entry.id === data.id);

        if (index !== -1) {
          newData[index] = {
            ...newData[index],
            ...data,
          };
        }

        return newData;
      });

      return data;
    },
    onError: (error) => {
      console.error('useUpdateAccessLicensesAdmin failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function useFetchTranslateClientsAdmin() {
  return useQuery({
    queryKey: [api.translateClientsAll],
    queryFn: async () => {
      return fetchWrapper.get(api.translateClientsAll);
    },
  });
}

export function useUpdateTranslateClientsAdmin() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => {
      return fetchWrapper.put(api.translateClients, body);
    },
    onSuccess: (data) => {
      notification.success('translateClients updated!');

      queryClient.setQueryData([api.translateClientsAll], (oldData) => {
        const newData = clone(oldData);
        const index = newData.findIndex((client) => client.id === data.id);
        newData[index] = data;
        return newData;
      });

      return data;
    },
    onError: (error) => {
      console.error('useUpdateTranslateClientsAdmin failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function useUpdateTranslateAdmin() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => {
      return fetchWrapper.put(api.translateClients, body);
    },
    onSuccess: (data) => {
      notification.success('translateClients updated!');

      queryClient.invalidateQueries({
        queryKey: [api.translateClients],
      });

      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_SEARCH],
      });

      return data;
    },
    onError: (error) => {
      console.error('useUpdateTranslateAdmin failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function usePostTranslateClientsAdmin() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => {
      return fetchWrapper.post(api.translateClients, body);
    },
    onSuccess: (data) => {
      notification.success('New translateClients created!');

      queryClient.invalidateQueries({
        queryKey: [api.translateClientsAll],
      });

      queryClient.invalidateQueries({
        queryKey: [api.translateClients],
      });

      return data;
    },
    onError: (error) => {
      console.error('usePostTranslateClientsAdmin failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function usePostOrganisationAdmin() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => {
      return fetchWrapper.post(api.organisations, body);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([api.organisations, 'all'], (oldData) => {
        const newData = clone(oldData) || [];
        newData.push(data);
        return newData;
      });

      notification.success('New organisation created!');

      return data;
    },
    onError: (error) => {
      console.error('usePostOrganisationAdmin failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function useUpdateOrganisationAdmin() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => {
      return fetchWrapper.put(api.organisations, body);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([api.organisations, 'all'], (oldData) => {
        const newData = clone(oldData);
        const index = newData.findIndex((org) => org.id === data.id);
        newData[index] = data;
        return newData;
      });

      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_SEARCH],
      });

      notification.success('Organisation updated!');

      return data;
    },
    onError: (error) => {
      console.error('useUpdateOrganisationAdmin failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function useCreateAuditLicenseAdmin() {
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => fetchWrapper.post(api.auditLicenses, body),
  });
}

export function useCreateAssistLicenseAdmin() {
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => fetchWrapper.post(api.assistLicenses, body),
  });
}

export function useCreateReportEntryAdmin() {
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => fetchWrapper.post(api.reportEntries, body),
  });
}

export function useUpdateReportEntryAdmin() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => fetchWrapper.put(api.reportEntries, body),
    onSuccess: (data) => {
      notification.success('Report entry updated!');

      queryClient.invalidateQueries({
        queryKey: [api.reportEntries],
      });

      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_SEARCH],
      });

      return data;
    },
    onError: (error) => {
      console.error('useUpdateReportEntryAdmin failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function useUpdateReportEntrySettings() {
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);
  const [, setAvailableAuditEntries] = useAtom(availableAuditEntriesAtom);

  const queryClient = useQueryClient();
  return useMutation({
    /** @param {Record<string, unknown>} body */
    mutationFn: (body) =>
      fetchWrapper.put(api.reportEntriesSettings, {
        ...body,
        id: currentAuditEntry.id,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [api.reportEntries],
      });

      setAvailableAuditEntries((oldData) => {
        return oldData.map((entry) => {
          if (entry.id === data.id) {
            return {
              ...entry,
              ...data,
            };
          }
          return entry;
        });
      });

      return data;
    },
    onError: (error) => {
      console.error('useUpdateReportEntrySettings failed', error);
    },
  });
}

export function useUpdateReportEntryRescan() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  const [, setAvailableAuditEntries] = useAtom(availableAuditEntriesAtom);

  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => fetchWrapper.put(api.reportEntryRescan, body),
    onSuccess: (data) => {
      notification.success('Success.');

      queryClient.invalidateQueries({
        queryKey: [api.reportEntryRescan],
      });

      setAvailableAuditEntries((oldData) => {
        const updatedData = oldData.map((entry) => {
          if (entry.id === data.id) {
            return {
              ...entry,
              ...data,
            };
          }
          return entry;
        });

        return updatedData;
      });

      return data;
    },
    onError: (error) => {
      console.error('useUpdateReportEntryRescan failed', error);
      notification.error(error?.message || error);
    },
  });
}

/**
 * @typedef {{ auditEntryId: number }} AuditEntryPinMutationVariables
 */
function updatePinnedAuditEntries(entries, auditEntryId, isPinned) {
  if (!Array.isArray(entries) || !entries.length) {
    return entries;
  }

  const remainingEntries = [];
  let updatedEntry;

  for (const entry of entries) {
    if (entry.id === auditEntryId) {
      updatedEntry = {
        ...entry,
        isPinned,
      };
      continue;
    }

    remainingEntries.push(entry);
  }

  if (!updatedEntry) {
    return entries;
  }

  if (isPinned) {
    return [updatedEntry, ...remainingEntries];
  }

  const pinnedEntries = [];
  const unpinnedEntries = [];

  for (const entry of remainingEntries) {
    if (entry.isPinned) {
      pinnedEntries.push(entry);
      continue;
    }

    unpinnedEntries.push(entry);
  }

  return [...pinnedEntries, updatedEntry, ...unpinnedEntries];
}

/** @returns {import('@tanstack/react-query').UseMutationResult<unknown, unknown, AuditEntryPinMutationVariables, unknown>} */
export function usePinAuditEntry() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [, setAvailableAuditEntries] = useAtom(availableAuditEntriesAtom);

  return useMutation({
    mutationFn: ({ auditEntryId }) => {
      return fetchWrapper.post(api.auditEntryPins, { auditEntryId });
    },
    onSuccess: (_data, { auditEntryId }) => {
      setAvailableAuditEntries((oldData) => {
        return updatePinnedAuditEntries(oldData, auditEntryId, true);
      });

      queryClient.invalidateQueries({ queryKey: [api.listAllLicenses] });
    },
    onError: (error) => {
      console.error('usePinAuditEntry failed', error);
      notification.error(error?.message ?? error);
    },
  });
}

/** @returns {import('@tanstack/react-query').UseMutationResult<unknown, unknown, AuditEntryPinMutationVariables, unknown>} */
export function useUnpinAuditEntry() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [, setAvailableAuditEntries] = useAtom(availableAuditEntriesAtom);

  return useMutation({
    mutationFn: ({ auditEntryId }) => {
      return fetchWrapper.delete(api.auditEntryPins, { auditEntryId });
    },
    onSuccess: (_data, { auditEntryId }) => {
      setAvailableAuditEntries((oldData) => {
        return updatePinnedAuditEntries(oldData, auditEntryId, false);
      });

      queryClient.invalidateQueries({
        queryKey: [api.listAllLicenses],
      });
    },
    onError: (error) => {
      console.error('useUnpinAuditEntry failed', error);
      notification.error(error?.message ?? error);
    },
  });
}

export function useUpdateAuditAssistLicenseAdmin(type) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);

  const url = type === 'assist' ? api.assistLicenses : api.auditLicenses;

  return useMutation({
    enabled: !!(user?.isAdmin || user?.isSalesAdmin || !!type),
    mutationFn: (body) => fetchWrapper.put(url, body),
    onSuccess: (data) => {
      notification.success(type + ' license updated!');

      queryClient.invalidateQueries({
        queryKey: [url],
      });

      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_SEARCH],
      });

      return data;
    },
    onError: (error) => {
      console.error('useUpdateAuditAssistLicenseAdmin failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function useSearchAdmin(endpoint, searchterm = '', delay = 1) {
  const [user] = useAtom(userAtom);
  const debouncedSearchQuery = useDebounce(searchterm, delay);
  return useQuery({
    retry: 0,
    keepPreviousData: true,
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    queryKey: [queryKeys.ADMIN_SEARCH, endpoint, debouncedSearchQuery],
    queryFn: () => {
      return new Promise((resolve) => {
        if (!debouncedSearchQuery) return resolve([]);
        fetchWrapper
          .get(api.searchBase + endpoint, {
            searchterm,
          })
          .then((data) => {
            resolve(data || []);
          });
      });
    },
  });
}

export function useFetchAllLicenses(params) {
  const [user] = useAtom(userAtom);
  return useQuery({
    enabled: !!(params?.userId && (user?.isAdmin || user?.isSalesAdmin)),
    queryKey: [queryKeys.ADMIN_SEARCH, api.searchAllLicenses, params],
    queryFn: () => fetchWrapper.get(api.searchAllLicenses, params),
  });
}

// --------------------------------------- Admin Queries ---------------------------------------

export function useFetchRuleMarkdown(ruleId, skip = false) {
  const { i18n, ready } = useInlineTranslation();
  const lng = i18n.resolvedLanguage;

  return useQuery({
    enabled: !!(ready && !skip && lng),
    queryKey: [queryKeys.RULE_MARKDOWN, lng, ruleId],
    queryFn: async () => {
      // Offline Support (not in use)
      /* if (offlineMode) {
                try {
                    const markdownModule = await import(`../../mockData/rules/${lng}/${ruleId}.md`);
                    const response = await fetch(markdownModule.default);
                    const text = await response.text();
                    return text;
                } catch (error) {
                    console.error('Error importing markdown file:', error);
                    throw new Error('Markdown file not found or could not be loaded.');
                }
            }
            else {
            } */
      return fetchWrapper.get(
        `${storageUrl}audit-json-files/v2/markdown/${lng}/${ruleId}.md`,
      );
    },
  });
}

// returns the data for all available reports (first 25 entries)
export function useFetchReportDataForAllDomains() {
  const [availableAuditEntries] = useAtom(availableAuditEntriesAtom);
  const [reportDemoMode] = useAtom(reportDemoModeAtom);

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const {
    eaRules,
    wcagRules,
    isError: rulesError,
    isLoading: rulesLoading,
  } = useFetchRules();

  const _queries = useMemo(() => {
    if (
      !Array.isArray(availableAuditEntries) ||
      availableAuditEntries.length === 0
    )
      return [];

    const sliceLength =
      availableAuditEntries.length > 500 ? 25 : availableAuditEntries?.length;

    return availableAuditEntries.slice(0, sliceLength).map((entry) => {
      const cdnDir = entry.cdnDirectory;
      const fullCdnPath =
        reportStorageUrl + cdnDir + (cdnDir.endsWith('/') ? '' : '/');

      return {
        queryKey: [queryKeys.REPORT, fullCdnPath],
        queryFn: async () => {
          if (offlineMode)
            return (await import('/mockData/report.json')).default;
          else return fetchWrapper.get(`${fullCdnPath}report.json`);
        },
        enabled: !!fullCdnPath,
      };
    });
  }, [availableAuditEntries]);

  const queries = useQueries({
    queries: _queries,
  });

  const queriesLoading =
    rulesLoading || queries.some((query) => query.isLoading);

  const queriesError = rulesError || queries.every((query) => query.isError);

  const AllReportsData = useMemo(() => {
    if (reportDemoMode) {
      setIsLoading(false);
      setIsError(false);
      return undefined;
    }

    if (!availableAuditEntries?.length || queriesLoading) {
      setIsLoading(true);
      setIsError(false);
      return undefined;
    }

    if (queriesError) {
      setIsError(true);
      setIsLoading(false);
      return undefined;
    }

    const allReports = {};
    let i = 0;

    for (const query of queries) {
      if (query.isError) {
        i++;
        continue;
      }
      const report = query.data;
      const conformanceTarget =
        availableAuditEntries[i].complianceTarget || 'AA';
      const auditEntry = availableAuditEntries[i];

      let data = {
        ...auditEntry,
        conformanceTarget,
      };

      if (report) {
        data = calculateAccessibilityData({
          report,
          history: null,
          conformanceTarget,
          eaRules,
          wcagRules,
          auditEntry,
        });
      }

      allReports[auditEntry.domain] = data;
      i++;
    }

    setIsLoading(false);
    setIsError(false);

    return allReports;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queriesLoading, queriesError, reportDemoMode, availableAuditEntries]);

  return {
    AllReportsData,
    isLoading,
    isError,
  };
}

export function useFetchPdfErrorText() {
  const { i18n } = useInlineTranslation();
  const lng = i18n.resolvedLanguage;

  const handleResponse = (response) => {
    return response.text().then((text) => {
      let data = text;

      if (text && response.headers.get('Content-Type')?.includes('json')) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          console.info('Error parsing JSON:', e);
          return Promise.reject({
            ok: false,
            status: 500,
            message: 'Invalid JSON response',
          });
        }
      }

      return data;
    });
  };

  const fallbackGetter = async () => {
    try {
      const response = await fetch(
        `${storageUrl}pdf_error_text_${lng}_v2.json`,
      );
      if (!response.ok) {
        throw new Error(
          `Error loading ${storageUrl}pdf_error_text_${lng}_v2.json`,
        );
      }
      return handleResponse(response);
    } catch (e) {
      const response = await fetch(`${storageUrl}pdf_error_text_en_v2.json`);
      if (!response.ok) {
        return Promise.reject(response);
      }
      return handleResponse(response);
    }
  };

  return useQuery({
    queryKey: [queryKeys.PDF_ERROR_TEXT, lng],
    queryFn: fallbackGetter,
    retry: false,
  });
}

export function useFetchFoundUrls(enabled = true) {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  return useQuery({
    enabled: !!(enabled && fullCdnPath),
    queryKey: [queryKeys.FOUND_URLS, fullCdnPath],
    queryFn: () => fetchWrapper.get(`${fullCdnPath}foundUrls.json`),
  });
}

/**
 * @returns {import('@tanstack/react-query').UseQueryResult<Record<string, string>, Error>}
 */
export function useFetchUrlMap(enabled = true) {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  return useQuery({
    enabled: !!(enabled && fullCdnPath),
    queryKey: [queryKeys.URL_MAP, fullCdnPath],
    queryFn: async () => {
      if (offlineMode) return (await import('/mockData/urlMap.json')).default;
      else return fetchWrapper.get(`${fullCdnPath}urlMap.json`);
    },
  });
}

export function useFetchAccess() {
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);

  return useQuery({
    queryKey: [api.access, currentAuditEntry.id],
    queryFn: async () =>
      fetchWrapper.get(api.access, { reportId: currentAuditEntry.id }),
  });
}

export function useAccessStorageFileExists(filePath) {
  const [reportDemoMode] = useAtom(reportDemoModeAtom);

  return useQuery({
    enabled: !!filePath || !reportDemoMode,
    queryKey: [queryKeys.ACCESS_STORAGE, filePath],
    queryFn: async () => {
      try {
        const result = await fetchWrapper.head(accessStorageUrl + filePath);
        return result.ok;
      } catch (error) {
        console.error('Error checking access storage file:', error);
        return false;
      }
    },
  });
}

export function useFetchAccessSuggestions(accessId) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    enabled: !!accessId,
    queryKey: [api.accessSuggestions, accessId],
    queryFn: async () => fetchWrapper.get(api.accessSuggestions, { accessId }),
  });

  const updateCache = useCallback(
    (updater) =>
      queryClient.setQueryData([api.accessSuggestions, accessId], (prev) =>
        typeof updater === 'function' ? updater(prev) : updater,
      ),
    [queryClient, accessId],
  );

  return { data, isLoading, isError, updateCache };
}

export function usePutAccessSettings() {
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);

  const { t } = useInlineTranslation();
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.put(api.accessSettings, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.access, currentAuditEntry.id],
        exact: true,
      });
      notification.success(t('settingsSaved'));
    },
  });
}

export function usePutAccessSetup() {
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);

  const queryClient = useQueryClient();

  return useMutation({
    /** @param {{ id: number, setupStatus: Partial<SetupChecklistState> }} body
     * @returns {Promise<void>}
     */
    mutationFn: (body) => fetchWrapper.put(api.accessSetup, body),
    onSuccess: (_data, { id, setupStatus }) => {
      queryClient.setQueryData([api.access, currentAuditEntry.id], (access) => {
        if (!access || access.id !== id) return access;

        return {
          ...access,
          setupStatus: {
            ...access.setupStatus,
            ...setupStatus,
          },
        };
      });
    },
  });
}

export function usePutAccessSuggestions() {
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.put(api.accessSuggestions, body),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [api.access, currentAuditEntry.id],
        exact: true,
      }),
  });
}

export function useResetAccess() {
  const { t } = useInlineTranslation();
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.put(api.accessSuggestionsReset, body),
    onSuccess: (_, { accessId }) => {
      queryClient.refetchQueries({
        queryKey: [api.accessSuggestions, accessId],
      });
      notification.success(t('successAccessReset'));
    },
  });
}

/**
 * @typedef {{ accessId: number }} AccessScriptBody
 */
/**
 * @returns {import('@tanstack/react-query').UseMutationResult<any, any, AccessScriptBody>}
 */
export function usePostAccessScript() {
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);

  const { t } = useInlineTranslation();
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.post(api.accessScript, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.access, currentAuditEntry.id],
      });
      notification.success(t('successAccessScriptUpdate'));
    },
  });
}

export function useFetchFonts(enabled = true) {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  return useQuery({
    enabled: !!(enabled && fullCdnPath),
    queryKey: [queryKeys.FONTS, fullCdnPath],
    queryFn: () => fetchWrapper.get(`${fullCdnPath}fonts.json`),
  });
}

export function useFetchLighthouseSummary() {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  return useQuery({
    queryKey: [queryKeys.LIGHTHOUSE_SUMMARY, fullCdnPath],
    queryFn: async () => {
      if (offlineMode)
        return (await import('/mockData/lighthouseSummary.json')).default;
      else
        return fetchWrapper.get(
          `${fullCdnPath}lighthouse/lighthouseSummary.json`,
        );
    },
  });
}

export function useFetchLighthouseReportForPage(filename) {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  return useQuery({
    queryKey: [queryKeys.LIGHTHOUSE, fullCdnPath, filename],
    queryFn: async () => {
      if (offlineMode)
        return (await import('/mockData/lighthouseDetails28.json')).default;
      else return fetchWrapper.get(`${fullCdnPath}lighthouse/${filename}.json`);
    },
  });
}

export function useFetchJobsSimpleLangDocument(options = {}) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [api.jobsSimpleLangDocument],
    queryFn: async () => {
      const data = await fetchWrapper.get(api.jobsSimpleLangDocument);
      void queryClient.invalidateQueries({ queryKey: [api.whoami] });
      return data;
    },
    ...options,
  });
}

export function useFetchPageDetailReport(pageIndex) {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  const url = `${fullCdnPath}reports/${pageIndex}.json`;

  return useQuery({
    enabled: pageIndex >= 0,
    queryKey: [queryKeys.PAGE_DETAIL_REPORT, fullCdnPath, pageIndex, url],
    queryFn: () => fetchWrapper.get(url),
  });
}

export function useFetchIgnoreIssues() {
  const [user] = useAtom(userAtom);

  return useQuery({
    // `user` keeps the cache per-user; the API reads the actual user from auth.
    queryKey: [api.ignoreIssues, user],
    queryFn: () => fetchWrapper.get(api.ignoreIssues),
  });
}

/** @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, { uid: string | number | null | undefined; auditEntryId: number | null }>} */
export function useDisplayIssues() {
  const { t } = useInlineTranslation();

  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.delete(api.ignoreIssues, body),
    onSuccess: () => {
      notification.success(t('noteDisplayIssueSuccess'));

      queryClient.invalidateQueries({ queryKey: [api.ignoreIssues] });
    },
    onError: (error) => {
      console.error('useUpdateAuditAssistLicenseAdmin failed', error);

      if (error.status === 400) {
        notification.error(t('noteDisplayIssueAlreadyDisplayed'));
      } else {
        notification.error(error?.message || error);
      }
    },
  });
}

/**
 * @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, {
 *   uid: string | number | null | undefined;
 *   elementUid: string | number;
 *   ignoreMode?: string;
 *   ruleId: number | null;
 *   html: string;
 *   ignoreAll: number;
 *   url: string;
 *   ignoreForAllDomains: number;
 *   orgId: number;
 *   userId: number;
 *   auditEntryId: number | null;
 * }>}
 */
export function useIgnoreIssues() {
  const { t } = useInlineTranslation();

  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.post(api.ignoreIssues, body),
    onSuccess: () => {
      notification.success(t('noteHideIssueSuccess'));

      queryClient.invalidateQueries({ queryKey: [api.ignoreIssues] });
    },
    onError: (error) => {
      console.error('useUpdateAuditAssistLicenseAdmin failed', error);

      if (error.status === 500) {
        notification.error(t('nodeHideIssueAlreadyIgnores'));
      } else {
        notification.error(error?.message || error);
      }
    },
  });
}

/**
 * @param {string} pdfName
 * @param {boolean} [userPdf]
 * @param {boolean} [org]
 * @param {string | null} [cdnDirectory] explicit report CDN directory (to read a
 *   detected PDF from a report other than the selected audit)
 */
export function useFetchPdfDetails(
  pdfName,
  userPdf = false,
  org = false,
  cdnDirectory = null,
) {
  let fullCdnPath;
  const [user] = useAtom(userAtom);
  const _fullUsersCdnPath = useAtom(fullUsersCdnPathAtom);
  const _fullCdnPath = useAtom(fullCdnPathAtom);
  if (org) {
    fullCdnPath = `${reportStorageUrl}org_storage/${user.primaryOrganisation}/PDF/improvedPDFs/`;
  } else if (cdnDirectory) {
    // Explicit report directory — used to read a detected PDF that belongs to a
    // report other than the currently-selected audit.
    fullCdnPath = `${reportStorageUrl}${cdnDirectory}${cdnDirectory.endsWith('/') ? '' : '/'}`;
  } else {
    [fullCdnPath] = userPdf ? _fullUsersCdnPath : _fullCdnPath;
  }
  return useQuery({
    queryKey: [queryKeys.PDFS, fullCdnPath, pdfName],
    queryFn: () =>
      fetchWrapper.get(`${fullCdnPath}PDF_Reports/${pdfName}.json`),
    refetchOnMount: 'always',
  });
}

export function useUpdateConformanceTarget() {
  const [user] = useAtom(userAtom);
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);
  const [, setAvailableAuditEntries] = useAtom(availableAuditEntriesAtom);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [api.conformanceTarget, currentAuditEntry?.id, user?.id],
    mutationFn: (newTarget) =>
      fetchWrapper.put(api.conformanceTarget, {
        newTarget: newTarget,
        entryId: currentAuditEntry.id,
      }),
    onSuccess: async (_, newTarget) => {
      setAvailableAuditEntries((oldData) => {
        return oldData.map((entry) => {
          // Only return a new object if the entry needs updating
          if (entry.id === currentAuditEntry.id) {
            return {
              ...entry, // Spread the existing properties
              complianceTarget: newTarget, // Update the complianceTarget
            };
          }
          return entry; // Return other entries unchanged
        });
      });

      localStorage.setItem('reload', new Date().getTime());

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.REPORT],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.HISTORY],
          refetchType: 'all',
        }),
      ]);
    },
  });
}

export function useFetchReportSettings() {
  const [settings, setSettings] = useState();
  const [_isLoading, setIsLoading] = useState(true);
  const [_isError, setIsError] = useState(false);
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  const { data, isLoading, isError } = useQuery({
    staleTime: 1000 * 90, // 90 seconds
    enabled: !!fullCdnPath,
    queryKey: [queryKeys.REPORT_SETTINGS, fullCdnPath],
    queryFn: () => fetchWrapper.get(fullCdnPath + 'config.json'),
  });

  useEffect(() => {
    if (isError) {
      setIsError(true);
      setIsLoading(false);
    } else if (!isError && !isLoading) {
      if (data) setSettings({ ...defaultReportSettings, ...data });
      else setSettings({ ...defaultReportSettings });

      setIsLoading(false);
      setIsError(false);
    }
  }, [isError, isLoading, data]);

  return { data: settings, isLoading: _isLoading, isError: _isError };
}

export function useFetchFoundUrlsHistory() {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  return useQuery({
    queryKey: [queryKeys.FOUND_URLS_HISTORY, fullCdnPath],
    queryFn: () => fetchWrapper.get(`${fullCdnPath}foundUrlsHistory.json`),
  });
}

export function useFetchNotifications() {
  const { i18n } = useInlineTranslation();
  let lng = i18n.resolvedLanguage;

  return useQuery({
    queryKey: [queryKeys.NOTIFICATIONS, lng],
    queryFn: () =>
      fetchWrapper.get(
        import.meta.env.DEV
          ? `/locales/${lng}/notifications.json`
          : `/${version}/locales/${lng}/notifications.json`,
      ),
  });
}

export function useFetchReportEntryConfig() {
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);
  return useQuery({
    enabled: !!currentAuditEntry?.id,
    queryKey: [api.reportEntryConfig, currentAuditEntry?.id],
    queryFn: () =>
      fetchWrapper.get(api.reportEntryConfig, {
        entryid: currentAuditEntry?.id,
      }),
  });
}

/**
 * updates the given report settings
 * only pass the changed values so that the backend can merge the new values with the old ones
 */
export function useUpdateReportSettings() {
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);
  const queryClient = useQueryClient();
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  return useMutation({
    /** @param {Record<string, unknown>} settingsToUpdate */
    mutationFn: (settingsToUpdate) => {
      settingsToUpdate.entryId = currentAuditEntry.id;
      return fetchWrapper.put(api.reportEntryConfig, settingsToUpdate);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [api.reportEntryConfig, currentAuditEntry?.id],
        data?.newConfig,
      );

      queryClient.invalidateQueries({
        queryKey: [queryKeys.REPORT_SETTINGS, fullCdnPath],
      });
    },
  });
}

//
export function useSamlLogin() {
  const [, setUser] = useAtom(userAtom);
  const [, setTokenExpires] = useAtom(tokenExpiresAtom);

  return useMutation({
    mutationFn: ({ token }) => {
      return fetch(api.samlLogin, {
        mode: 'cors',
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
        }),
      }).then(async (response) => {
        if (!response.ok || response.status > 299) {
          // Extract and log the error message from the response body
          const errorData = await response.json();

          // If the backend provides a specific error message, throw it
          if (errorData.error) {
            throw new Error(errorData.error);
          }

          // Otherwise, throw a generic error message
          throw new Error('Login failed due to an unknown error.');
        }

        // save the dev token for development in the browser
        if (!import.meta.env.PROD && response.headers.get('X-Dev-Token')) {
          localStorage.setItem(
            'X-Dev-Token',
            response.headers.get('X-Dev-Token'),
          );
        }

        const user = await response.json();

        user.fullName = `${user.firstName + ' ' || ''}${user.lastName || ''}`;

        setUser(user);
        setTokenExpires(user.tokenExpires);

        return user;
      });
    },
  });
}

export function useGetOidcConfig() {
  return useMutation({
    mutationFn: ({ email }) => {
      const url = new URL(api.oidcConfig);
      url.searchParams.append('email', email);

      return fetch(url.toString(), {
        mode: 'cors',
        method: 'GET',
        cache: 'no-cache',
      }).then(async (response) => {
        if (!response.ok || response.status > 299) {
          const errorData = await response.json();
          console.log('Error response from server:', errorData.error);

          if (errorData.error) {
            throw new Error(errorData.error);
          }

          throw new Error('Failed to fetch OIDC config.');
        }

        const config = await response.json();
        return config;
      });
    },
  });
}

export function useLoginUser() {
  const [, setUser] = useAtom(userAtom);
  const [, setTokenExpires] = useAtom(tokenExpiresAtom);

  return useMutation({
    mutationFn: ({ password, email }) => {
      if (offlineMode) {
        const user = demoUser;

        setUser(user);
        setTokenExpires(user.tokenExpires);

        return Promise.resolve(user);
      }

      return fetch(api.login, {
        mode: 'cors',
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      }).then(async (response) => {
        if (!response.ok || response.status > 299) {
          throw new Error('Login failed');
        }

        // save the dev token for development in the browser
        if (!import.meta.env.PROD && response.headers.get('X-Dev-Token')) {
          localStorage.setItem(
            'X-Dev-Token',
            response.headers.get('X-Dev-Token'),
          );
        }

        const user = await response.json();

        user.fullName = `${user.firstName + ' ' || ''}${user.lastName || ''}`;

        setUser(user);
        setTokenExpires(user.tokenExpires);

        return user;
      });
    },
  });
}

export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword, token }) =>
      fetchWrapper.post(api.changePwd, {
        currentPassword,
        newPassword,
        token,
      }),
  });
}

export function usePasswordLostMutation() {
  return useMutation({
    /**
     * @param {{ email: string }} param0
     */
    mutationFn: ({ email }) =>
      fetchWrapper.post(api.pwLost, {
        email,
      }),
  });
}

export function useCreateMultipleUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.post(api.multipleUsers, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.orgUsers],
      });
      queryClient.invalidateQueries({
        queryKey: [api.users],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_SEARCH, 'users'],
      });
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.post(api.users, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.orgUsers],
      });
      queryClient.invalidateQueries({
        queryKey: [api.users],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_SEARCH, 'users'],
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [api.securityLogs],
      });
    },
  });
}

export function useRemoveOrgUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deleteApiKey) => {
      return fetchWrapper.delete(api.users, {
        deleteApiKey,
      });
    },
    onSuccess: (_data, _userId) => {
      queryClient.invalidateQueries({
        queryKey: [api.orgUsers],
      });
      queryClient.invalidateQueries({
        queryKey: [api.users],
      });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.users, body);
    },
    onSuccess: () => {
      // refetch whoami, whose queryFn updates userAtom for the whole app
      // (header, sidebar, ...) in case the user edited their own profile
      queryClient.invalidateQueries({
        queryKey: [api.whoami],
      });
      queryClient.invalidateQueries({
        queryKey: [api.orgUsers],
      });
      queryClient.invalidateQueries({
        queryKey: [api.users],
      });
      // validate the admin search query to update the user list and licenses if user selected
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_SEARCH],
      });
    },
  });
}

/** @returns {import('@tanstack/react-query').UseQueryResult<Array<{ type: number, licenseId: number | string, accessLevel?: import('@eye-able/types').AccessLevel }>, Error>} */
export function useFetchLicenseConstraints(userid) {
  return useQuery({
    enabled: !!userid,
    queryKey: [api.licenseConstraints, userid],
    queryFn: () =>
      fetchWrapper.get(api.licenseConstraints, {
        userid: userid,
      }),
  });
}

/** @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, { userId: number, licenseData: import('@eye-able/types').TeamAdministrationRight[] }, unknown>} */
export function usePostLicenseConstraints() {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: ({ userId, licenseData }) => {
      return fetchWrapper.post(api.licenseConstraints, {
        licenseData,
        userId,
      });
    },
    onSuccess: (data, { userId }) => {
      queryClient.setQueryData([api.licenseConstraints, userId], data);
      queryClient.invalidateQueries({
        queryKey: [api.orgUsers],
      });
    },
    onError: (error) => {
      console.error('usePostLicenseConstraints failed', error);
      notification.error(error?.message || error);
    },
  });
}

/** @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, { userId: number }, unknown>} */
export function useRemoveLicenseConstraints() {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: ({ userId }) => {
      return fetchWrapper.delete(api.licenseConstraints, {
        userId,
      });
    },
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: [api.licenseConstraints, userId],
      });
      queryClient.invalidateQueries({
        queryKey: [api.orgUsers],
      });
    },
    onError: (error) => {
      console.error('useRemoveLicenseConstraints failed', error);
      notification.error(error?.message || error);
    },
  });
}

/**
 * @typedef {import('@eye-able/types').OrganisationUserData} DashboardOrgUser
 */

/** @typedef {import('@eye-able/types').OrganisationUserTeamData} DashboardOrgUserTeam */
/** @typedef {import('@eye-able/types').OrganisationUserAccessSummary} DashboardOrgUserAccessSummary */
/** @typedef {{ includeTeams?: boolean, includeAccessSummary?: boolean }} DashboardOrgUserQueryOptions */

/** @typedef {import('@eye-able/types').TeamData} DashboardTeam */
/** @typedef {import('@eye-able/types').TeamDetailsData} DashboardTeamDetails */
/** @typedef {import('@eye-able/types').TeamListData} DashboardTeamListItem */
/** @typedef {import('@eye-able/types').TeamRightsData} DashboardTeamRights */
/** @typedef {import('@eye-able/types').OrganisationEntitlementCatalogData} DashboardOrganisationEntitlementCatalog */
/** @typedef {{ organisationId: number, name: string, description: string | null, color: string | null, memberIds?: number[], licenseData?: import('@eye-able/types').TeamAdministrationRight[] }} DashboardCreateTeamBody */
/** @typedef {{ teamId: number, name: string, description: string | null, color: string }} DashboardUpdateTeamBody */
/** @typedef {{ teamId: number }} DashboardDeleteTeamBody */
/** @typedef {{ teamId: number, userId: number }} DashboardAddTeamMemberBody */

/**
 * @param {DashboardOrgUserQueryOptions} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<DashboardOrgUser[], Error>}
 */
export function useFetchOrgUsers(options = {}) {
  const [user] = useAtom(userAtom);
  const [demoMode] = useAtom(demoModeAtom);
  const includeTeams = options.includeTeams === true;
  const includeAccessSummary = options.includeAccessSummary === true;
  const reportId = options.reportId ?? null;
  const enabled = options.enabled !== false;

  return useQuery({
    enabled,
    queryKey: [
      api.orgUsers,
      user?.primaryOrganisation,
      demoMode,
      includeTeams,
      includeAccessSummary,
      reportId,
    ],
    queryFn: () => {
      /** @type {{ orgId: number, includeTeams?: true, includeAccessSummary?: true, reportId?: number }} */
      const params = {
        orgId: user?.primaryOrganisation,
      };

      if (includeTeams) {
        params.includeTeams = true;
      }

      if (includeAccessSummary) {
        params.includeAccessSummary = true;
      }

      if (reportId) {
        params.reportId = reportId;
      }

      return demoMode
        ? Promise.resolve([demoUser])
        : fetchWrapper.get(api.orgUsers, params);
    },
  });
}

/**
 * @param {number} orgId
 * @param {'primary' | 'secondary'} [type='primary']
 * @param {DashboardOrgUserQueryOptions} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<DashboardOrgUser[], Error>}
 */
export function useFetchOrgUsersAdmin(orgId, type = 'primary', options = {}) {
  const [user] = useAtom(userAtom);
  const includeTeams = options.includeTeams === true;
  const includeAccessSummary = options.includeAccessSummary === true;

  return useQuery({
    queryKey: [api.orgUsers, orgId, type, includeTeams, includeAccessSummary],
    enabled: !!(user?.isAdmin || user?.isSalesAdmin),
    queryFn: () => {
      /** @type {{ orgId: number, type: 'primary' | 'secondary', includeTeams?: true, includeAccessSummary?: true }} */
      const params = {
        orgId,
        type,
      };

      if (includeTeams) {
        params.includeTeams = true;
      }

      if (includeAccessSummary) {
        params.includeAccessSummary = true;
      }

      return fetchWrapper.get(api.orgUsers, params);
    },
  });
}

export function useFetchHubspotCompanies(query) {
  return useQuery({
    queryKey: [api.hubspotCompanies, query],
    queryFn: () => fetchWrapper.get(api.hubspotCompanies, { q: query }),
    enabled: !!(query && query.length >= 2),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * @param {number | null | undefined} [organisationId]
 * @param {{ enabled?: boolean }} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<DashboardTeamListItem[], Error>}
 */
export function useFetchTeams(organisationId, options = {}) {
  const [user] = useAtom(userAtom);
  const normalizedOrganisationId =
    organisationId == null ? null : Number(organisationId);

  return useQuery({
    enabled: options.enabled ?? enabledTeamAdmin(user),
    queryKey: [api.teams, normalizedOrganisationId],
    queryFn: async () => {
      /** @type {DashboardTeamListItem[]} */
      const teams = await fetchWrapper.get(api.teams);

      if (!normalizedOrganisationId) {
        return teams;
      }

      return teams.filter(
        (team) => Number(team.organisationId) === normalizedOrganisationId,
      );
    },
  });
}

/**
 * @param {number | null | undefined} teamId
 * @param {{ enabled?: boolean, includeMembers?: boolean, includeRights?: boolean }} [options]
 * @returns {import('@tanstack/react-query').UseQueryResult<DashboardTeamDetails, Error>}
 */
export function useFetchTeamDetails(teamId, options = {}) {
  const [user] = useAtom(userAtom);
  const normalizedTeamId = teamId == null ? null : Number(teamId);
  const enabled = options.enabled !== false;
  const includeMembers = options.includeMembers === true;
  const includeRights = options.includeRights === true;

  return useQuery({
    enabled: enabledTeamAdmin(user) && enabled && !!normalizedTeamId,
    queryKey: [
      api.teamDetails,
      normalizedTeamId,
      includeMembers,
      includeRights,
    ],
    queryFn: async () => {
      /** @type {{ teamId: number, includeMembers?: boolean, includeRights?: boolean }} */
      const params = { teamId: normalizedTeamId };

      if (includeMembers) {
        params.includeMembers = true;
      }

      if (includeRights) {
        params.includeRights = true;
      }

      /** @type {DashboardTeamDetails} */
      const teamDetails = await fetchWrapper.get(api.teamDetails, params);

      return teamDetails;
    },
  });
}

/**
 * @param {number | null | undefined} organisationId
 * @param {boolean} [enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult<DashboardOrganisationEntitlementCatalog, Error>}
 */
export function useFetchOrganisationEntitlementCatalog(
  organisationId,
  enabled = true,
) {
  const [user] = useAtom(userAtom);
  const normalizedOrganisationId =
    organisationId == null ? null : Number(organisationId);

  return useQuery({
    enabled: enabledTeamAdmin(user) && enabled && !!normalizedOrganisationId,
    queryKey: [api.organisationEntitlementCatalog, normalizedOrganisationId],
    queryFn: () =>
      fetchWrapper.get(api.organisationEntitlementCatalog, {
        organisationId: normalizedOrganisationId,
      }),
  });
}

const TEAM_MUTATION_MESSAGES = {
  create: 'teamCreated',
  update: 'teamUpdated',
  delete: 'teamDeleted',
  teamMembersUpdated: 'teamMembersUpdated',
  replaceRights: 'teamRightsUpdated',
};

function resolveTeamMutationNotifications(options, defaultSuccessMessage) {
  return {
    notifyOnError: options?.notifyOnError !== false,
    notifyOnSuccess: options?.notifyOnSuccess !== false,
    successMessage: options?.successMessage ?? defaultSuccessMessage,
  };
}

function invalidateTeamQueries(queryClient) {
  return Promise.all(
    [
      api.teams,
      api.orgUsers,
      api.teamDetails,
      api.teamMembers,
      api.teamRights,
      api.teamEffectiveMembers,
      api.userEffectiveRights,
    ].map((queryKey) =>
      queryClient.invalidateQueries({
        queryKey: [queryKey],
        refetchType: 'all',
      }),
    ),
  );
}

/** @returns {import('@tanstack/react-query').UseMutationResult<DashboardTeam, Error, DashboardCreateTeamBody, unknown>} */
export function useCreateTeam(options) {
  const { t } = useInlineTranslation();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  const { notifyOnError, notifyOnSuccess, successMessage } =
    resolveTeamMutationNotifications(options, TEAM_MUTATION_MESSAGES.create);

  return useMutation({
    enabled: enabledTeamAdmin(user),
    mutationFn: (body) => fetchWrapper.post(api.teams, body),
    onSuccess: (data) => {
      invalidateTeamQueries(queryClient);
      if (notifyOnSuccess) {
        notification.success(t(`${successMessage}`));
      }

      return data;
    },
    onError: (error) => {
      console.error('useCreateTeam failed', error);
      if (notifyOnError) {
        notification.error(error?.message || error);
      }
    },
  });
}

/** @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, DashboardUpdateTeamBody, unknown>} */
export function useUpdateTeam(options) {
  const { t } = useInlineTranslation();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  const { notifyOnError, notifyOnSuccess, successMessage } =
    resolveTeamMutationNotifications(options, TEAM_MUTATION_MESSAGES.update);

  return useMutation({
    enabled: enabledTeamAdmin(user),
    mutationFn: (body) => fetchWrapper.put(api.teams, body),
    onSuccess: (data) => {
      invalidateTeamQueries(queryClient);
      if (notifyOnSuccess) {
        notification.success(t(`${successMessage}`));
      }

      return data;
    },
    onError: (error) => {
      console.error('useUpdateTeam failed', error);
      if (notifyOnError) {
        notification.error(error?.message || error);
      }
    },
  });
}

/** @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, DashboardDeleteTeamBody, unknown>} */
export function useDeleteTeam(options) {
  const { t } = useInlineTranslation();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  const { notifyOnError, notifyOnSuccess, successMessage } =
    resolveTeamMutationNotifications(options, TEAM_MUTATION_MESSAGES.delete);

  return useMutation({
    enabled: enabledTeamAdmin(user),
    mutationFn: ({ teamId }) =>
      fetchWrapper.delete(api.teams, {
        teamId,
      }),
    onSuccess: (data) => {
      invalidateTeamQueries(queryClient);
      if (notifyOnSuccess) {
        notification.success(t(`${successMessage}`));
      }
      return data;
    },
    onError: (error) => {
      console.error('useDeleteTeam failed', error);
      if (notifyOnError) {
        notification.error(error?.message || error);
      }
    },
  });
}

/** @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, DashboardAddTeamMemberBody, unknown>} */
export function useAddTeamMember(options) {
  const { t } = useInlineTranslation();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  const { notifyOnError, notifyOnSuccess, successMessage } =
    resolveTeamMutationNotifications(
      options,
      TEAM_MUTATION_MESSAGES.teamMembersUpdated,
    );

  return useMutation({
    enabled: enabledTeamAdmin(user),
    mutationFn: (body) => fetchWrapper.post(api.teamMembers, body),
    onSuccess: (data) => {
      invalidateTeamQueries(queryClient);
      if (notifyOnSuccess) {
        notification.success(t(`${successMessage}`));
      }
      return data;
    },
    onError: (error) => {
      console.error('useAddTeamMember failed', error);
      if (notifyOnError) {
        notification.error(error?.message || error);
      }
    },
  });
}

/** @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, DashboardAddTeamMemberBody, unknown>} */
export function useRemoveTeamMember(options) {
  const { t } = useInlineTranslation();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  const { notifyOnError, notifyOnSuccess, successMessage } =
    resolveTeamMutationNotifications(
      options,
      TEAM_MUTATION_MESSAGES.teamMembersUpdated,
    );

  return useMutation({
    enabled: enabledTeamAdmin(user),
    mutationFn: ({ teamId, userId }) =>
      fetchWrapper.delete(api.teamMembers, {
        teamId,
        userId,
      }),
    onSuccess: (data) => {
      invalidateTeamQueries(queryClient);
      if (notifyOnSuccess) {
        notification.success(t(`${successMessage}`));
      }
      return data;
    },
    onError: (error) => {
      console.error('useRemoveTeamMember failed', error);
      if (notifyOnError) {
        notification.error(error?.message || error);
      }
    },
  });
}

/** @returns {import('@tanstack/react-query').UseMutationResult<DashboardTeamRights, Error, DashboardTeamRights, unknown>} */
export function useReplaceTeamRights(options) {
  const { t } = useInlineTranslation();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);
  const { notifyOnError, notifyOnSuccess, successMessage } =
    resolveTeamMutationNotifications(
      options,
      TEAM_MUTATION_MESSAGES.replaceRights,
    );

  return useMutation({
    enabled: enabledTeamAdmin(user),
    mutationFn: (body) => fetchWrapper.put(api.teamRights, body),
    onSuccess: (data) => {
      invalidateTeamQueries(queryClient);
      if (notifyOnSuccess) {
        notification.success(t(`${successMessage}`));
      }
      return data;
    },
    onError: (error) => {
      console.error('useReplaceTeamRights failed', error);
      if (notifyOnError) {
        notification.error(error?.message || error);
      }
    },
  });
}

/**
 * @typedef {import('@eye-able/types').OrganizationData} DashboardOrganisation
 */

/**
 * fetches all organisations for the current user or all for admins if no id is provided
 * @returns {import('@tanstack/react-query').UseQueryResult<DashboardOrganisation[], Error>}
 */
export function useFetchOrganisations(id = 'all', { enabled = true } = {}) {
  return useQuery({
    queryKey: [api.organisations, id],
    queryFn: () =>
      fetchWrapper.get(api.organisations, id === 'all' ? {} : { id }),
    enabled,
  });
}

export function useFetchOrgLicenses() {
  return useQuery({
    queryKey: [api.orgLicenses],
    queryFn: () => fetchWrapper.get(api.orgLicenses),
  });
}

export function useUpdateOrgSettings() {
  const [user] = useAtom(userAtom);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.orgSettings, {
        ...body,
        id: user.primaryOrganisation,
      });
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({
        queryKey: [api.organisations],
      });
    },
  });
}

export function usePostSupportRequest() {
  const { t, i18n } = useInlineTranslation();
  const notification = useNotification();
  const [user] = useAtom(userAtom);

  return useMutation({
    /** @param {string} message
     * @return {Promise<void>}
     */
    mutationFn: (message) =>
      fetchWrapper.post(api.supportMail, {
        mailLang: user?.mailLang || i18n.resolvedLanguage,
        comment: message,
      }),
    onSuccess: (_data) => {
      notification.success(t('supportRequestSent'));
    },
  });
}

export function usePostAssistTrialLicense() {
  const [user] = useAtom(userAtom);
  const { i18n } = useInlineTranslation();

  const defaults = {
    newUser: null,
    createServerConfig: true,
    mailLang: user?.mailLang || i18n.resolvedLanguage,
    userId: user?.id,
    primaryOrganisation: user?.primaryOrganisation,
  };

  return useMutation({
    /** @param {{ domain: string, comment: string }} body
     * @return {Promise<void>}
     */
    mutationFn: (body) =>
      fetchWrapper.post(api.assistTestLicense, {
        ...defaults,
        ...body,
      }),
  });
}

export function usePostTodo() {
  const [selectedDomain] = useAtom(selectedDomainAtom);
  const [, setTodosData] = useAtom(todosDataAtom);

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.todo, { ...body, domain: selectedDomain });
    },
    onSuccess: (data) => {
      if (data?.newId) {
        setTodosData(data.newTodos);
        return data.newTodos;
      }
    },
  });
}

export function useUpdateTodos() {
  const [, setTodosData] = useAtom(todosDataAtom);

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.todos, body);
    },
    onSuccess: (data) => {
      delete data.message;
      setTodosData(data);
      return data;
    },
  });
}

export function useRemoveTodo() {
  const [, setTodosData] = useAtom(todosDataAtom);

  return useMutation({
    mutationFn: (todoId) => {
      return fetchWrapper.delete(api.todo, {
        todoId: todoId,
      });
    },
    onSuccess: (_data, todoId) => {
      let nextTodosData;

      setTodosData((currentTodosData) => {
        nextTodosData = clone(currentTodosData);

        delete nextTodosData?.todos[todoId];
        return nextTodosData;
      });

      return nextTodosData;
    },
  });
}

export function useFetchSpellCheckingData(enabled = true) {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  return useQuery({
    enabled: !!fullCdnPath && enabled,
    queryKey: [queryKeys.SPELL_CHECKING, fullCdnPath],
    queryFn: () =>
      fetchWrapper.get(`${fullCdnPath}Content_Analyse/spellchecker.json`),
  });
}

export function useFetchSpellCheckingHistory() {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);

  return useQuery({
    queryKey: [queryKeys.SPELL_CHECKING_HISTORY, fullCdnPath],
    queryFn: () =>
      fetchWrapper.get(
        `${fullCdnPath}Content_Analyse/spellchecker_history.json`,
      ),
  });
}

export function useFetchSpellCheckingReadabilityScore() {
  const [fullCdnPath] = useAtom(fullCdnPathAtom);
  return useQuery({
    queryKey: [queryKeys.SPELL_CHECKING_READABILITY_SCORE, fullCdnPath],
    queryFn: () =>
      fetchWrapper.get(`${fullCdnPath}Content_Analyse/readabilityScores.json`),
  });
}

export function useFetchGeneratorEntries(cdnDirectory) {
  return useQuery({
    queryKey: [api.accessibilityStatement, cdnDirectory],
    queryFn: () =>
      fetchWrapper.get(api.accessibilityStatement, { cdnDirectory }),
  });
}

export function usePostGeneratorEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.post(api.accessibilityStatement, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.accessibilityStatement],
      });
    },
  });
}

export function useFetchFromStorage(cdnDirectory, fileName, fallbackFile) {
  const fetchFile = (name) => {
    return fetchWrapper
      .get(`${storageUrl}${cdnDirectory}/${name}`)
      .catch(() => {
        console.error(`Error fetching file: ${name}`);
        if (name !== fallbackFile && fallbackFile) {
          return fetchFile(fallbackFile);
        }
        throw new Error(`Error fetching file: ${name}`);
      });
  };

  return useQuery({
    enabled: !!cdnDirectory && !!fileName,
    queryKey: [queryKeys.STORAGE_FILES, cdnDirectory, fileName],
    queryFn: () => fetchFile(fileName),
  });
}

export function usePostJiraConfig() {
  const notification = useNotification();
  const { t } = useInlineTranslation();
  const [, setJiraConfig] = useAtom(jiraConfigAtom);

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.jiraConfig, body);
    },
    onSuccess: (data) => {
      setJiraConfig(data);
      return data;
    },
    onError: async (error) => {
      const errorText = await error.json();
      let notifyText = t('jiraConfigPostFailed');
      if (errorText && errorText?.message)
        notifyText += ': ' + t(errorText.message);
      notification.error(notifyText);

      return null;
    },
  });
}

export function useUpdateJiraConfig() {
  const notification = useNotification();
  const { t } = useInlineTranslation();
  const [, setJiraConfig] = useAtom(jiraConfigAtom);

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.jiraConfig, body);
    },
    onSuccess: (data) => {
      setJiraConfig(data);
      return data;
    },
    onError: async (error) => {
      const errorText = await error.json();
      let notifyText = t('jiraConfigUpdateFailed');
      if (errorText && errorText?.message)
        notifyText += ': ' + t(errorText.message);
      notification.error(notifyText);

      return null;
    },
  });
}

export function useRemoveJiraConfig() {
  const notification = useNotification();
  const [, setJiraConfig] = useAtom(jiraConfigAtom);
  const [, setTodosData] = useAtom(todosDataAtom);

  return useMutation({
    mutationFn: () => {
      return fetchWrapper.delete(api.jiraConfig);
    },
    onSuccess: (data) => {
      setTodosData(data);
      setJiraConfig(undefined);
    },
    onError: (error) => {
      console.error('useRemoveJiraConfig failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function useFetchJiraProjects() {
  const [jiraConfig] = useAtom(jiraConfigAtom);
  return useQuery({
    enabled: !!jiraConfig && jiraConfig.isValid,
    queryKey: [api.jiraProjects],
    queryFn: () => fetchWrapper.get(api.jiraProjects),
  });
}

export function useFetchJiraUsersProjectAssignable(projectKeys) {
  return useQuery({
    enabled: !!projectKeys,
    queryKey: [api.jiraUsers, projectKeys],
    queryFn: () =>
      fetchWrapper.get(api.jiraUsersProjectAssignable, {
        projectKeys: projectKeys,
      }),
  });
}

export function useFetchJiraIssueTypes(projectKey) {
  return useQuery({
    enabled: !!projectKey,
    queryKey: [api.jiraIssueTypes, projectKey],
    queryFn: () =>
      fetchWrapper.get(api.jiraIssueTypes, {
        projectKey: projectKey,
      }),
  });
}

export function useFetchJiraComponents(projectKey, enabled) {
  return useQuery({
    enabled,
    queryKey: [api.jiraComponents, projectKey],
    queryFn: () =>
      fetchWrapper.get(api.jiraComponents, {
        projectKey: projectKey,
      }),
  });
}

export function useFetchJiraIssueMetaData(projectKey, issuetypeId) {
  return useQuery({
    enabled: !!projectKey && issuetypeId !== 0,
    queryKey: [api.jiraIssueMetaData, projectKey, issuetypeId],
    queryFn: () =>
      fetchWrapper.get(api.jiraIssueMetaData, {
        projectKey,
        issuetypeId,
      }),
  });
}

export function usePostJiraIssue() {
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.jiraIssue, body);
    },
    onSuccess: (data) => {
      return data;
    },
  });
}

export function usePostJiraIssueComment() {
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.jiraIssueComment, body);
    },
    onSuccess: (data) => {
      return data;
    },
  });
}

export function usePostJiraIssueTransition() {
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.jiraIssueTransition, body);
    },
  });
}

export function usePostMulJiraIssues() {
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.jiraIssues, body);
    },
    onSuccess: (data) => {
      return data;
    },
  });
}

export function useFetchTranslationDomains(fetchAll = false) {
  const [user] = useAtom(userAtom);
  const apiUrl =
    user.isAdmin && fetchAll ? api.translateClientsAll : api.transDomainSearch;
  return useQuery({
    queryKey: [apiUrl],
    queryFn: () => fetchWrapper.get(apiUrl),
    enabled: !!user?.id,
    refetchInterval: 5 * 60 * 1000, // refetch every 5 minutes
  });
}

export function useFetchTranslationLangPairs(params) {
  return useQuery({
    enabled: !!params.domainId,
    queryKey: [api.transDomainLangPairs, params],
    queryFn: () => fetchWrapper.get(api.transDomainLangPairs, params),
    refetchInterval: 5 * 60 * 1000, // refetch every 5 minutes
  });
}

export function useFetchSubpageSearch(params, options = {}) {
  return useQuery({
    queryKey: [api.transSubpageSearch, params],
    queryFn: () => fetchWrapper.get(api.transSubpageSearch, params),
    ...options,
  });
}

export function useFetchTranslationSearch(params, options = {}) {
  return useQuery({
    queryKey: [api.translationSearch, params],
    queryFn: () => fetchWrapper.get(api.translationSearch, params),
    ...options,
  });
}

export function useUpdateTranslation(queryParams) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.translationsManual, body);
    },
    onSuccess: () => {
      notification.success(t('successUpdateTranslation'));
      queryClient.invalidateQueries({
        queryKey: [api.transDomainAllTranslation, queryParams],
      });
    },
  });
}

export function useRemoveTranslation(queryParams) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.delete(api.translationsManual, body);
    },
    onSuccess: () => {
      notification.success(t('successRemoveTranslation'));
      queryClient.invalidateQueries({
        queryKey: [api.transDomainAllTranslation, queryParams],
      });
    },
  });
}

export function useDeleteTranslation(queryParams) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.delete(api.translationSingleReference, body);
    },
    onSuccess: () => {
      notification.success(t('successDeleteTranslation'));
      queryClient.invalidateQueries({
        queryKey: [api.transDomainAllTranslation, queryParams],
      });
    },
  });
}

export function useCreatePdf() {
  return useMutation({
    mutationFn: ({
      html,
      title,
      language,
      margin = undefined,
      test = true,
      _binaryOnly = false,
    }) => {
      return fetchWrapper.post(api.createPdf, {
        html,
        title,
        language,
        margin,
        test,
      });
    },
    onSuccess: (blob, props) => {
      if (props.binaryOnly) return blob;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', props.title + '.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    },
  });
}

export function useFetchGeneratorFiles(cdnDirectory, entries) {
  const { i18n } = useInlineTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const _queries = useMemo(() => {
    if (!entries || !cdnDirectory) {
      return [];
    }

    return entries.map((entry) => {
      return {
        queryKey: [queryKeys.GENERATOR_FILES, cdnDirectory, entry.name],
        queryFn: () =>
          fetchWrapper.get(
            `${reportStorageUrl}${cdnDirectory}/accessibilityStatements/${entry.name}?t=${Date.now()}`,
          ),
      };
    });
  }, [cdnDirectory, entries]);

  const queries = useQueries({
    queries: _queries,
  });

  const queriesLoading = queries.some((query) => query.isLoading);

  const queriesError = queries.some((query) => query.isError);

  const data = useMemo(() => {
    if (queriesError) {
      setIsError(true);
      setIsLoading(false);
      return [];
    }
    if (queriesLoading) {
      setIsLoading(true);
      return [];
    }

    setIsLoading(false);
    setIsError(false);

    const rows = [];

    if (!queries.length) return rows;

    for (const i in queries) {
      const file = queries[i].data;
      const data = entries[i];

      if (!data || !file) continue;

      const name = data.name.replace('.json', '');

      const [lang, wcagLvl, countryOrig] = name.split('_');
      let country = countryOrig;
      if (!countryOrig) {
        country = 'de';
      }
      const row = {
        language: lang,
        wcagLvl,
        country,
        dateChanged: formatDateToLocale(
          data.lastChanged,
          i18n.resolvedLanguage,
        ),
        name: data.name,
        file,
      };

      rows.push(row);
    }

    return rows;
  }, [queriesLoading, queriesError, entries]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = () => {
    queries.forEach((query) => query.refetch());
  };

  return {
    data,
    isLoading,
    isError,
    refetch,
  };
}

export function useFetchTranscriptionJobs() {
  return useQuery({
    queryKey: [api.transcription],
    queryFn: () => fetchWrapper.get(api.transcription),
    refetchInterval: 30 * 1000, // refetch every 30 seconds
  });
}

export function useRemoveTranscriptionJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId) => {
      return fetchWrapper.delete(api.transcription, {
        jobId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.transcription],
      });
    },
  });
}

export function usePostTranscriptionJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.post(api.transcription, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.transcription],
      });
    },
  });
}

export function useFetchGuidedTestingMetaData(cdnDirectory) {
  const [reportDemoMode] = useAtom(reportDemoModeAtom);
  return useQuery({
    enabled: !!cdnDirectory || !reportDemoMode,
    queryKey: [queryKeys.GUIDED_TESTING_FILES, cdnDirectory],
    queryFn: () =>
      fetchWrapper.get(
        `${reportStorageUrl}${cdnDirectory}/guidedTestingMetadata.json`,
      ),
  });
}

export function useFetchGuidedTestsOverview() {
  const [reportDemoMode] = useAtom(reportDemoModeAtom);
  return useQuery({
    queryKey: [queryKeys.GUIDED_TESTING_TESTS_OVERVIEW],
    queryFn: () =>
      fetchWrapper.get(
        `${storageUrl}guided-test-assests/guidedTestsOverview.json`,
      ),
    enabled: !reportDemoMode,
  });
}

export function useFetchGuidedTestsEN301549() {
  const [reportDemoMode] = useAtom(reportDemoModeAtom);
  const { i18n } = useInlineTranslation();
  const lng = i18n.resolvedLanguage;
  return useQuery({
    queryKey: [queryKeys.GUIDED_TESTING_TESTS_OVERVIEW, lng],
    queryFn: async () => {
      try {
        return await fetchWrapper.get(
          `${storageUrl}guided-test-assests/en301549/en301549_${lng}.json`,
        );
      } catch (error) {
        console.warn(
          `Language file "${lng}" not found, falling back to "en".`,
          error,
        );
        return await fetchWrapper.get(
          `${storageUrl}guided-test-assests/en301549/en301549_en.json`,
        );
      }
    },
    enabled: !reportDemoMode,
  });
}

export function useFetchWCAGRules() {
  const { i18n } = useInlineTranslation();
  const lng = i18n.resolvedLanguage;
  const [reportDemoMode] = useAtom(reportDemoModeAtom);
  return useQuery({
    queryKey: [queryKeys.WCAG_RULES, lng],
    queryFn: async () => {
      if (offlineMode)
        return (await import(`../../mockData/wcag_rules_${lng}.json`)).default;
      else
        return fetchWrapper.get(
          `${storageUrl}audit-json-files/wcag_rules_${lng}.json`,
        );
    },
    enabled: !reportDemoMode,
  });
}

export function useFetchAccStateText() {
  return useQuery({
    queryKey: [queryKeys.ACC_STATES_TEXT],
    queryFn: async () => {
      return fetchWrapper.get(`${storageUrl}acc-statement/states_text_v3.json`);
    },
  });
}
export function useFetchAccOtherText() {
  return useQuery({
    queryKey: [queryKeys.ACC_OTHER_TEXT],
    queryFn: async () => {
      return fetchWrapper.get(`${storageUrl}acc-statement/other_text_v3.json`);
    },
  });
}

export function usePostManualTestSchema() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.manualTestSchema, body);
    },
    onSuccess: (_res, _props) => {
      queryClient.invalidateQueries({
        queryKey: [api.manualTestSchema],
      });
    },
  });
}

export function useRemoveManualTestSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ schemaId }) => {
      return fetchWrapper.delete(api.manualTestSchema, {
        schemaId,
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [api.manualTestSchema],
      });
      return res;
    },
  });
}

export function usePostManualTestComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.manualTestComment, body);
    },
    onSuccess: (res, props) => {
      queryClient.invalidateQueries({
        queryKey: [api.manualTest, +props.testId],
      });
      return res;
    },
  });
}

export function useUpdateManualTestComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.manualTestComment, body);
    },
    onSuccess: (res, props) => {
      queryClient.invalidateQueries({
        queryKey: [api.manualTest, +props.testId],
      });
      return res;
    },
  });
}

export function useResolveAllManualTestComments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.manualTestCommentResolveAll, body);
    },
    onSuccess: (res, props) => {
      queryClient.invalidateQueries({
        queryKey: [api.manualTest, +props.testId],
      });
      return res;
    },
  });
}

export function useRemoveManualTestComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ testId, testKey, commentId }) => {
      return fetchWrapper.delete(api.manualTestComment, {
        testId,
        testKey,
        commentId,
      });
    },
    onSuccess: (res, props) => {
      queryClient.invalidateQueries({
        queryKey: [api.manualTest, +props.testId],
      });
      return res;
    },
  });
}

function invalidateManualTestLists(queryClient) {
  queryClient.invalidateQueries({ queryKey: [api.manualTestPublished] });
  queryClient.invalidateQueries({ queryKey: [api.manualTestUnpublished] });
}

export function usePostManualTest() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.manualTest, body);
    },
    onSuccess: (res, _props) => {
      invalidateManualTestLists(queryClient);
      queryClient.invalidateQueries({
        queryKey: [api.manualTestSchema],
      });
      if (res.newId) navigate(`/report/manual-tests/test/${res.newId}`);
    },
  });
}

export function usePublishManualTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.manualTestPublish, body);
    },
    onSuccess: () => invalidateManualTestLists(queryClient),
  });
}

/**
 * @typedef {Object} ManualTestExportBody
 * @property {number|string} testId
 * @property {'docx'|'pdf'} format
 */

/**
 * @returns {import('@tanstack/react-query').UseMutationResult<Blob, any, ManualTestExportBody>}
 */
export function useGenerateManualTestExport() {
  return useMutation({
    mutationFn: ({ testId, format }) => {
      return fetchWrapper.post(api.manualTestExport, { format }, { testId });
    },
  });
}

export function useRasterizeManualTestIssueSample() {
  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(api.manualTestRasterize, body);
    },
  });
}

export function useUpdateManualTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.manualTest, body);
    },
    onSuccess: (res, props) => {
      queryClient.invalidateQueries({
        queryKey: [api.manualTest, +props.testId],
      });
      invalidateManualTestLists(queryClient);
      return res;
    },
  });
}

/**
 * @typedef {Object} BulkManualTestBody
 * @property {number} testId
 * @property {string[]} testKeys
 * @property {string} status
 */

/**
 * @returns {import('@tanstack/react-query').UseMutationResult<any, any, BulkManualTestBody>}
 */
export function useBulkUpdateManualTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.manualTestBulk, body);
    },
    onSuccess: (res, props) => {
      queryClient.invalidateQueries({
        queryKey: [api.manualTest, +props.testId],
      });
      invalidateManualTestLists(queryClient);
      return res;
    },
  });
}

export function useUpdateManualTestScope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.put(api.manualTestScope, body);
    },
    onSuccess: async (res, props) => {
      await queryClient.invalidateQueries({
        queryKey: [api.manualTest, +props.testId],
      });
      await queryClient.refetchQueries({
        queryKey: [api.manualTest, +props.testId, 'draft'],
        exact: true,
      });
      invalidateManualTestLists(queryClient);
      return res;
    },
  });
}

export function useRemoveManualTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ testId }) => {
      return fetchWrapper.delete(api.manualTest, {
        testId,
      });
    },
    onSuccess: () => {
      invalidateManualTestLists(queryClient);
      queryClient.invalidateQueries({
        queryKey: [api.manualTestSchema],
      });
    },
  });
}

export function useFetchAllGuidedTests() {
  const [reportDemoMode] = useAtom(reportDemoModeAtom);
  return useQuery({
    queryKey: [api.guidedTestAll],
    queryFn: () => fetchWrapper.get(api.guidedTestAll),
    enabled: !reportDemoMode,
  });
}

export function useFetchGuidedTest({ testId }) {
  const [reportDemoMode] = useAtom(reportDemoModeAtom);
  return useQuery({
    enabled: !!testId || !reportDemoMode,
    queryKey: [api.guidedTest, +testId],
    queryFn: () => fetchWrapper.get(api.guidedTest, { testId }),
  });
}

/**
 * @typedef {Object} GuidedTestBody
 * @property {number | string | undefined} auditEntryId
 * @property {string | undefined} cdnDirectory
 */

/**
 * @returns {import('@tanstack/react-query').UseMutationResult<any, any, GuidedTestBody>}
 */
export function usePostGuidedTest() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: ({ auditEntryId, cdnDirectory }) => {
      return fetchWrapper.post(api.guidedTest, {
        auditEntryId,
        cdnDirectory,
      });
    },
    onSuccess: (res, _props) => {
      queryClient.invalidateQueries({
        queryKey: [api.guidedTestAll],
      });
      if (res.newId) navigate(`/report/tests/test/${res.newId}`);
    },
  });
}

/**
 * @typedef {Object} RemoveGuidedTestBody
 * @property {number} testId
 */

/**
 * @returns {import('@tanstack/react-query').UseMutationResult<any, any, RemoveGuidedTestBody>}
 */
export function useRemoveGuidedTest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ testId }) => {
      return fetchWrapper.delete(api.guidedTest, {
        testId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.guidedTestAll],
      });
    },
  });
}

export function useFetchConfig(domain, product = 'translate') {
  const apiEndpoint =
    product === 'translate' ? 'translateConfig' : 'plainConfig';
  const wholeUrl = api.translate_plain_apiUrl + apiEndpoint;
  return useQuery({
    enabled: !!domain,
    queryKey: [wholeUrl, domain],
    queryFn: () => fetchWrapper.get(wholeUrl, { domain }),
  });
}

export function useUpdateTranslateConfig(product = 'translate') {
  const queryClient = useQueryClient();
  const apiEndpoint =
    product === 'translate' ? 'translateConfig' : 'plainConfig';
  const wholeUrl = api.translate_plain_apiUrl + apiEndpoint;

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.post(wholeUrl, body);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [wholeUrl, variables.domain],
      });
    },
  });
}

export function useDeleteTranslateConfig(product = 'translate') {
  const queryClient = useQueryClient();
  const apiEndpoint =
    product === 'translate' ? 'translateConfig' : 'plainConfig';
  const wholeUrl = api.translate_plain_apiUrl + apiEndpoint;

  return useMutation({
    mutationFn: (body) => {
      return fetchWrapper.delete(wholeUrl, body);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [wholeUrl, variables.domain],
      });
    },
  });
}

export function useUpdateOrgIpAddresses() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();
  const [user] = useAtom(userAtom);

  return useMutation({
    enabled: !!(user?.isOrgAdmin || user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => {
      return fetchWrapper.put(api.orgIps, body);
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [api.organisations, +id] });

      notification.success(t('allowedIpsInputUpdated'));

      return data;
    },
    onError: (error) => {
      console.error('Update Organisation IpAddresses failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function useUpdateUserSettings() {
  const { t } = useInlineTranslation();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [user] = useAtom(userAtom);

  return useMutation({
    enabled: !!(user?.isOrgAdmin || user?.isAdmin || user?.isSalesAdmin),
    mutationFn: (body) => {
      return fetchWrapper.put(api.usersettings, body);
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [api.organisations, +id] });
      notification.success(t('settingsUpdated'));
      return data;
    },
    onError: (error) => {
      console.error('Update Organisation MaxDaysSinceLogin failed', error);
      notification.error(error?.message || error);
    },
  });
}

export function useSearchHelp(query = '', language, delay = 300) {
  const debouncedSearchQuery = useDebounce(query, delay);
  return useQuery({
    retry: 0,
    keepPreviousData: true,
    queryKey: [queryKeys.HELP_SEARCH, debouncedSearchQuery],
    queryFn: () => {
      return new Promise((resolve) => {
        if (!debouncedSearchQuery) return resolve([]);
        fetchWrapper
          .get(api.helpCenterSearch, { query, language })
          .then((data) => {
            resolve(data || []);
          });
      });
    },
  });
}
export function useFetchSupportThreads() {
  return useQuery({
    queryKey: [api.supportThreads],
    queryFn: () => fetchWrapper.get(api.supportThreads),
    refetchIntervalInBackground: false,
    refetchInterval: 60 * 1000, // refetch every 1 minute
  });
}

export function useFetchSupportThreadMessages(conversationsThreadId) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [api.supportThreadMessages, conversationsThreadId],
    queryFn: () => {
      return new Promise((resolve) => {
        if (!conversationsThreadId) return resolve([]);
        fetchWrapper
          .get(api.supportThreadMessages, { conversationsThreadId })
          .then((data) => {
            queryClient.invalidateQueries({ queryKey: [api.supportThreads] });
            resolve(data || []);
          });
      });
    },
    enabled: !!conversationsThreadId,
    staleTime: 0,
  });
}

export function useUpdateSupportThread() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body) =>
      fetchWrapper.post(api.supportThreads, body, null, null),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [api.supportThreads] });

      notification.success(t('supportRequestSuccess'));

      return data;
    },
  });
}

export function useUpdateSupportThreadStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.put(api.supportThreadStatus, body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [api.supportThreads] });

      return data;
    },
  });
}

export function useUpdateSupportThreadMessage() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body) =>
      fetchWrapper.put(api.supportThreadMessages, body, null, null),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [api.supportThreadMessages, data.conversationsThreadId],
      });

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [api.supportThreadMessages, data.conversationsThreadId],
        });
      }, 3000);

      notification.success(t('supportRequestSuccess'));

      return data;
    },
  });
}

export function useFetchReportSubscriptions() {
  return useQuery({
    queryKey: [api.reportSubscription],
    queryFn: () => fetchWrapper.get(api.reportSubscription),
    refetchInterval: 30 * 1000, // refetch every 30 seconds
  });
}

export function useRemoveReportSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (auditEntryId) => {
      return fetchWrapper.delete(api.reportSubscription, {
        auditEntryId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.reportSubscription],
      });
    },
  });
}

export function usePostReportSubscription() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body) => fetchWrapper.post(api.reportSubscription, body),
    onSuccess: () => {
      notification.success(t('reportSubscriptionCreated'));
      queryClient.invalidateQueries({
        queryKey: [api.reportSubscription],
      });
    },
  });
}

export function useFetchPartnerContingent(orgId) {
  const [user] = useAtom(userAtom);

  return useQuery({
    enabled: enabledAdminPartner(user),
    queryKey: [api.partnerContingents, orgId],
    queryFn: () =>
      !orgId ? {} : fetchWrapper.get(api.partnerContingents, { orgId }),
  });
}

export function usePostPartnerContingent() {
  const { t } = useInlineTranslation();
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.post(api.partnerContingents, body),
    onSuccess: (data) => {
      queryClient.setQueryData(
        [api.partnerContingents, data.partnersOrgId],
        data,
      );
      notification.success(t('successPartnerContingentsCreate'));
    },
  });
}

export function usePutPartnerContingent() {
  const { t } = useInlineTranslation();
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => fetchWrapper.put(api.partnerContingents, body),
    onSuccess: (data) => {
      queryClient.setQueryData(
        [api.partnerContingents, data.partnersOrgId],
        data,
      );
      notification.success(t('successPartnerContingentsUpdate'));
    },
  });
}

export function useFetchPartnerManagementUsers() {
  const [user] = useAtom(userAtom);

  return useQuery({
    enabled: enabledAdminPartner(user),
    queryKey: [api.partnerManagementUsers],
    queryFn: () => fetchWrapper.get(api.partnerManagementUsers),
  });
}

export function useFetchPartnerManagementOrganisations() {
  const [user] = useAtom(userAtom);

  return useQuery({
    enabled: enabledAdminPartner(user),
    queryKey: [api.partnerManagementOrganisations],
    queryFn: () => fetchWrapper.get(api.partnerManagementOrganisations),
  });
}

export function usePostPartnerManagementOrganisation() {
  const { t } = useInlineTranslation();
  const notification = useNotification();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) =>
      fetchWrapper.post(api.partnerManagementOrganisations, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.partnerManagementOrganisations],
      });

      notification.success(t('successOrgCreate'));
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [api.securityLogs],
      }),
  });
}

export function useFetchPartnerManagementLicenses(product = 'all') {
  const [user] = useAtom(userAtom);

  return useQuery({
    enabled: enabledAdminPartner(user),
    queryKey: [api.partnerManagementLicense, product],
    queryFn: () =>
      fetchWrapper.get(`${api.partnerManagementLicense}/${product}`),
  });
}

export function usePostPartnerManagementLicense() {
  return useMutation({
    mutationFn: ({ product, body }) =>
      fetchWrapper.post(`${api.partnerManagementLicense}/${product}`, body),
  });
}
export function usePutPartnerManagementLicense() {
  return useMutation({
    mutationFn: ({ product, body }) =>
      fetchWrapper.put(`${api.partnerManagementLicense}/${product}`, body),
    onError: (data) => console.error('usePutPartnerContingents failed', data),
  });
}

// ---- Glossary queries ----

export function useFetchGlossary(params) {
  return useQuery({
    enabled: !!params.domainId,
    queryKey: [api.glossary, params],
    queryFn: () => fetchWrapper.get(api.glossary, params),
    staleTime: 2 * 60 * 1000,
    retry: false, // GET returns 400 when no glossary exists – don't retry
  });
}

export function useFetchGlossaryAvailableLangs() {
  return useQuery({
    queryKey: [api.glossaryAvailableLangs],
    queryFn: () => fetchWrapper.get(api.glossaryAvailableLangs),
    staleTime: 60 * 60 * 1000, // 60 min
  });
}

// ---- Glossary Dictionary queries ----

export function useFetchGlossaryDictionary(params, options = {}) {
  return useQuery({
    enabled:
      !!params.domainId &&
      !!params.sourceLang &&
      !!params.targetLang &&
      options.enabled !== false,
    queryKey: [api.glossaryDictionary, params],
    queryFn: () => fetchWrapper.get(api.glossaryDictionary, params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    keepPreviousData: true,
  });
}

export function useCreateGlossary(queryParams) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body) => fetchWrapper.post(api.glossary, body),
    onSuccess: () => {
      notification.success(t('successCreateGlossary'));
      // Invalidate both glossary existence and dictionary entries
      queryClient.invalidateQueries({ queryKey: [api.glossary] });
      queryClient.invalidateQueries({
        queryKey: [api.glossaryDictionary, queryParams],
      });
    },
    onError: (error) => {
      console.error('useCreateGlossary', error);
      const msg = error?.message || '';
      if (msg.includes('is not supported')) {
        notification.error(t('glossarTargetLangNotSupported'));
      } else {
        notification.error(t('errorCreateGlossary'));
      }
    },
  });
}

export function useUpdateGlossaryDictionary(queryParams) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body) => fetchWrapper.put(api.glossaryDictionary, body),
    onSuccess: () => {
      notification.success(t('successUpdateGlossary'));
      queryClient.invalidateQueries({
        queryKey: [api.glossaryDictionary, queryParams],
      });
    },
    onError: (error) => {
      console.error('useUpdateGlossaryDictionary', error);
      notification.error(t('errorUpdateGlossary'));
    },
  });
}

export function useDeleteGlossaryDictionary(queryParams) {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (params) => fetchWrapper.delete(api.glossaryDictionary, params),
    onSuccess: () => {
      notification.success(t('successDeleteGlossary'));
      queryClient.invalidateQueries({
        queryKey: [api.glossaryDictionary, queryParams],
      });
      queryClient.invalidateQueries({ queryKey: [api.glossary] });
      queryClient.invalidateQueries({ queryKey: [api.glossaryAvailableLangs] });
    },
    onError: (error) => {
      console.error('useDeleteGlossaryDictionary', error);
      notification.error(t('errorDeleteGlossary'));
    },
  });
}
